import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { ReactComponent as EyeIcon } from "../../../../assets/svgs/eye-icon.svg";
import { SearchBar } from "../../../../components/Elements/Search/SearchBar";
import { Tables } from "../../../../components/Elements/Tables/Tables";
import TextStyles from "../../../../components/Elements/TextStyles/TextStyles";
import { Tag, Spin } from "antd";
import {
  fetchManagedForms,
  resetManagedForms,
} from "../../../../redux/ManageForms/managedFormsSlice";

const ManageForms = ({
  basePath = "/organization/projects/standard-forms/view",
  enableEmployeeApi = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(
    location.state?.currentPage || 1,
  );

  useEffect(() => {
    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        currentPage,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, navigate, location.pathname]);
  const { managedForms, loading, error } = useSelector(
    (state) => state.managedForms,
  );

  // Reverted to original Mock Data but removing bold styling
  // const mockData = [
  //   {
  //     id: "1",
  //     projectName: "OGB017/FOOTPRINT CALCULATION -SCOPE 1 & 2/0326/022",
  //     formName: "BASIC DETAILS FORM",
  //     scopeType: "LOCATION (LS1)",
  //     subScope: "DELHI",
  //     assignedBy: "BERNARD",
  //     status: "Active",
  //   },
  //   {
  //     id: "2",
  //     projectName: "OGB017/CARBON CALCULATOR/0326/021",
  //     formName: "SCOPE 1",
  //     scopeType: "LOCATION (LS1)",
  //     subScope: "PUNE",
  //     assignedBy: "LEE",
  //     status: "In Progress",
  //   },
  //   {
  //     id: "3",
  //     projectName: "OGB017/CARBON CALCULATOR/0326/021",
  //     formName: "SCOPE 1 & 2",
  //     scopeType: "DEPARTMENT (LS1)",
  //     subScope: "HR",
  //     assignedBy: "CALVIN",
  //     status: "Completed",
  //   },
  //   {
  //     id: "4",
  //     projectName: "OGB017/FOOTPRINT CALCULATION -SCOPE 1 & 2/0326/022",
  //     formName: "SCOPE 3",
  //     scopeType: "LOCATION (LS2)",
  //     subScope: "HYDERABAD",
  //     assignedBy: "RONALD",
  //     status: "Paused",
  //   },
  //   {
  //     id: "5",
  //     projectName: "OGB017/FOOTPRINT CALCULATION -SCOPE 1/0326/020",
  //     formName: "EMPLOYEE FORM",
  //     scopeType: "LOCATION (LS1)",
  //     subScope: "GURUGRAM",
  //     assignedBy: "SOHAM",
  //     status: "In Progress",
  //   },
  //   {
  //     id: "6",
  //     projectName: "OGB017/CARBON CALCULATOR/0326/021",
  //     formName: "BASIC DETAILS FORM",
  //     scopeType: "DEPARTMENT (LS2)",
  //     subScope: "SALES",
  //     assignedBy: "CODY",
  //     status: "Active",
  //   },
  //   {
  //     id: "7",
  //     projectName: "OGB017/FOOTPRINT CALCULATION -SCOPE 1 & 2/0326/022",
  //     formName: "BRSR",
  //     scopeType: "LOCATION (LS1)",
  //     subScope: "MUMBAI",
  //     assignedBy: "JACOB",
  //     status: "In Progress",
  //   },
  //   {
  //     id: "8",
  //     projectName: "OGB017/FOOTPRINT CALCULATION -SCOPE 1/0326/020",
  //     formName: "SCOPE 1 & 2",
  //     scopeType: "LOCATION (LS2)",
  //     subScope: "BANGALORE",
  //     assignedBy: "LESLIE",
  //     status: "Completed",
  //   },
  //   {
  //     id: "9",
  //     projectName: "OGB017/FOOTPRINT CALCULATION -SCOPE 1/0326/020",
  //     formName: "SCOPE 3",
  //     scopeType: "DEPARTMENT (LS2)",
  //     subScope: "MARKETING",
  //     assignedBy: "GREGORY",
  //     status: "Paused",
  //   },
  // ];

  useEffect(() => {
    if (enableEmployeeApi) {
      dispatch(fetchManagedForms());
    } else {
      dispatch(resetManagedForms());
    }
  }, [dispatch, enableEmployeeApi]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
      case "active":
        return "#E6F4EA"; // Green
      case "In Progress":
      case "in_progress":
        return "#FEF7E0"; // Yellow
      case "Completed":
      case "completed":
        return "#E8F0FE"; // Blue
      case "Paused":
      case "paused":
        return "#FCE8E6"; // Red
      default:
        return "#F1F3F4";
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case "Active":
      case "active":
        return "#137333";
      case "In Progress":
      case "in_progress":
        return "#B06000";
      case "Completed":
      case "completed":
        return "#1967D2";
      case "Paused":
      case "paused":
        return "#C5221F";
      default:
        return "#5F6368";
    }
  };

  const headers = [
    { label: "Sr. No.", key: "serial", width: "4%" },
    { label: "Project Name", key: "projectName", width: "22%" },
    { label: "Form Name", key: "formName", width: "16%" },
    { label: "Scope Type", key: "scopeType", width: "14%" },
    { label: "Sub-Scope", key: "subScope", width: "12%" },
    { label: "Assigned By", key: "assignedBy", width: "12%" },
    { label: "Status", key: "status", width: "12%" },
    { label: "Action", key: "action", width: "8%" },
  ];

  const filteredData = useMemo(() => {
    // const sourceData = enableEmployeeApi ? managedForms || [] : mockData;
    const sourceData = enableEmployeeApi ? managedForms : [];
    return sourceData
      .filter(
        (item) =>
          (item.projectName || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (item.formId.title || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      )
      .map((item, index) => {
        const getDisplayValue = (val) => {
          if (!val) return "N/A";
          if (typeof val === "string" || typeof val === "number") return val;
          if (typeof val === "object") {
            return (
              val.title ||
              val.name ||
              val.location_name ||
              val.department_name ||
              val._id ||
              "N/A"
            );
          }
          return "N/A";
        };

        const locationValue = getDisplayValue(item.locationId);
        const departmentValue = getDisplayValue(item.departmentId);

        const projectNameValue = getDisplayValue(
          item.projectName || item.projectId,
        );
        const formNameValue = getDisplayValue(
          item.formName || item.formId || item.formType,
        );

        const formIdValue =
          typeof item.formId === "object" ? item.formId?._id : item.formId;
        console.log("formIdValue:", formIdValue, "item.formId:", item.formId);

        const resolvedFormIdValue =
          formIdValue && formIdValue !== item._id
            ? formIdValue
            : typeof item.formId === "object"
              ? item.formId?._id
              : formIdValue;
        const allotmentIdValue = item._id;
        const formTitleValue =
          typeof item.formId === "object"
            ? item.formId?.title
            : item.formTitle || item.formName || null;
        const roleTypeValue =
          typeof item.formId === "object" ? item.formId?.role_type : null;

        return {
          ...item,
          serial: (
            <div className="text-center font-normal text-gray-600">
              {index + 1}
            </div>
          ),
          projectName: (
            <div className="text-center text-gray-600 font-normal break-words whitespace-normal">
              {projectNameValue || "N/A"}
            </div>
          ),

          formName: (
            <div className="text-center text-gray-600 font-normal break-words whitespace-normal">
              {formNameValue || "N/A"}
            </div>
          ),

          scopeType: (
            <div className="text-center text-gray-600 font-normal break-words whitespace-normal">
              {item.scopeType ||
                (item.locationId ? "LOCATION" : null) ||
                (item.departmentId ? "DEPARTMENT" : null) ||
                (item.role ? "ROLE" : null) ||
                "N/A"}
              {item.allottedByRole ? ` (${item.allottedByRole})` : ""}
            </div>
          ),

          subScope: (
            <div className="text-center text-gray-600 font-normal break-words whitespace-normal">
              {item.subScope ||
                locationValue ||
                departmentValue ||
                item.role ||
                "N/A"}
            </div>
          ),
          assignedBy: (
            <div className="text-center text-gray-600 font-normal">
              {item.clientId?.name || "N/A"}
            </div>
          ),
          status: (
            <div className="flex justify-center">
              <Tag
                style={{
                  backgroundColor: getStatusColor(item.status),
                  color: getStatusTextColor(item.status),
                  border: `1px solid ${getStatusTextColor(item.status)}30`,
                  borderRadius: "8px",
                  padding: "2px 12px",
                  fontWeight: "400",
                  fontSize: "10px",
                  maxWidth: "100%",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {item.status}
              </Tag>
            </div>
          ),
          action: (
            <div className="flex justify-center">
              <EyeIcon
                className="w-5 h-5 cursor-pointer hover:scale-110 transition-all"
                onClick={() =>
                  navigate(`${basePath}/${allotmentIdValue}`, {
                    state: {
                      projectName: projectNameValue,
                      formName: formNameValue,
                      formTitle: formTitleValue || formNameValue,
                      formType: item.formType || null,
                      allotmentType: item.allotmentType || null,
                      projectId:
                        typeof item.projectId === "object"
                          ? item.projectId?._id
                          : item.projectId || item.project_id || null,
                      formId: resolvedFormIdValue || null,
                      formIdRaw: formIdValue || null,
                      formAllotmentId: allotmentIdValue,
                      roleType: roleTypeValue,
                      status: item.status,
                      source: "manage-forms",
                      templateId: item.templateId || item.template_id || null,
                      userId: item.userId || item.user_id || null,
                      progress: item.progress || null,
                      frequecy: item.frequency || null,
                      periods: item.periods || null,
                      period: item.period || null,
                      locationId:
                        typeof item.locationId === "object"
                          ? item.locationId?._id
                          : item.locationId || null,
                      departmentId:
                        typeof item.departmentId === "object"
                          ? item.departmentId?._id
                          : item.departmentId || null,
                      locationIdRaw: item.locationId || null,
                      departmentIdRaw: item.departmentId || null,
                      allottedByRole: item.allottedByRole || null,
                      subAllotmentMode: item.sub_allotment_mode || null,
                      manageLevel: item.scopeType?.includes("LS1")
                        ? 1
                        : item.scopeType?.includes("LS2")
                          ? 2
                          : item.locationId
                            ? 1
                            : 2,
                    },
                  })
                }
              />
            </div>
          ),
        };
      });
  }, [searchTerm, navigate, enableEmployeeApi, managedForms, basePath]);

  return (
    <div className="md:p-6 lg:p-0 bg-[#F5F5F5] min-h-screen">
      <style>{`
      .responsive-forms-table table {
        table-layout: fixed;
        width: 100% !important;
        min-width: 0 !important;
      }
      .responsive-forms-table th,
      .responsive-forms-table td {
        word-break: break-word;
        overflow-wrap: break-word;
        white-space: normal !important;
      }
      .responsive-forms-table {
        overflow-x: visible !important;
      }
    `}</style>
      <div className="flex flex-wrap justify-end text-[10px] font-medium gap-1 text-gray-400 mb-2">
        <p>Home</p>
        <span>/</span>
        <p>Projects</p>
        <span>/</span>
        <TextStyles style={{ color: "#06513D", fontSize: "10px" }}>
          Manage Forms
        </TextStyles>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <IoArrowBackCircleOutline
          onClick={() => navigate(-1)}
          className="w-8 h-8 text-[#06513D] hover:scale-105 transition cursor-pointer"
        />
        <h1 className="text-[#06513D] font-bold text-xl">Manage Forms</h1>
      </div>

      <div
        style={{
          border: "1px solid #3BA2B959",
          borderRadius: "10px",
          paddingTop: "20px",
          boxShadow: "0px 0px 16px 0px #3BA2B91F",
          backgroundColor: "#fff",
        }}
      >
        <div className="flex justify-between px-3 mb-6">
          <div className="flex gap-4 pl-9 items-center">
            <SearchBar
              width="332px"
              placeholder="Search"
              onSearch={(val) => setSearchTerm(val)}
            />
          </div>
        </div>

        <div className="responsive-forms-table">
          {enableEmployeeApi && loading ? (
            <div className="flex justify-center items-center py-16">
              <Spin size="large" />
            </div>
          ) : enableEmployeeApi && error ? (
            <div className="flex justify-center items-center py-16 text-red-500 text-sm">
              {error}
            </div>
          ) : (
            <Tables
              headers={headers}
              data={filteredData}
              pagination={true}
              pageSize={10}
              current={currentPage}
              onChange={(page) => setCurrentPage(page)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageForms;
